/**
 * Per-document-family metadata that drives the guided full-capture flow:
 * a friendly Brazilian-Portuguese name, an icon, and — crucially — whether the
 * document has a back side worth photographing and whether/where it carries a QR
 * code we can read.
 *
 * The classifier emits keys like `cnh-front`, `rg-back`, `cin-open`,
 * `cin-digital`, `certidaonasc`. We key this table on the *family* (the part
 * before the first dash) and read the side/variant from the suffix.
 */

/** What we know about a document family for the purpose of guiding capture. */
export interface DocMeta {
  /** Friendly name shown (and spoken) to the user, in pt-BR. */
  nome: string;
  /** Material icon representing the document. */
  icon: string;
  /** Does this document have a back side worth a second photo? */
  temVerso: boolean;

  // --- Per-family capture plan (drives the state machine) -------------------
  // This makes routing table-driven: where (if anywhere) a decodable QR lives
  // and how it's read, plus whether the CPF is mandatory or may be legitimately
  // absent (acknowledged downstream, in the provider).

  /**
   * The single source of truth for QR handling — **which physical surface** the
   * decodable QR lives on, and therefore how it's read. Drives both the state
   * machine (verso-foto → focused `qr` step) and the still-image QR read in
   * `finalizar`/`lerQr`:
   *  - `'none'`  — no QR to read;
   *  - `'back'`  — QR on the outer back, the same surface we photograph as the
   *    back → a focused live-QR step follows the back photo (camera path), or the
   *    QR is read from the back still (upload path);
   *  - `'inner'` — QR on an **inner** surface that is NOT the outer back: the CNH
   *    is folded, so its QR/MRZ page only appears once the document is opened and
   *    turned over. This surface is read for its data but is **never** submitted as
   *    the document back — Veriff wants the outer back (the vehicle-categories
   *    side) and rejects the inner page with "Document back missing" (609);
   *  - `'front'` — single-sheet QR on the front → focused QR after the front;
   *  - `'pdf'`   — QR lives in an uploaded digital PDF (handled by the digital path).
   */
  qr: 'none' | 'back' | 'inner' | 'front' | 'pdf';
  /**
   * CPF policy: `'required'` = a valid CPF must be present (CNH/CIN, whose QR
   * carries it); `'ackIfMissing'` = CPF may be legitimately absent and the
   * provider routes to an acknowledgment instead of hard-rejecting.
   */
  cpf: 'required' | 'ackIfMissing';
}

/**
 * Family → metadata. Choices reflect the real Brazilian documents:
 *  - CIN (new national ID): front + back, with a signed QR on the **back** — the
 *    QR surface and the back surface are the same piece of card.
 *  - CNH (driver's licence): the **physical** document is folded in half and has
 *    THREE surfaces, not two. Closed, the front is the photo/data side and the
 *    back is the **vehicle-categories** side (ACC/A/B/C, observações, local).
 *    The QR/MRZ page is *inside*: you have to open the document and turn it over.
 *    Front and back are what Veriff verifies; the inner QR page is read for its
 *    signed data only (`qr: 'inner'`) and must never be sent as the back —
 *    Veriff answers "Document back missing" (609) if it is.
 *    The CNH-e (digital/app printout, the `-digital` variant) is a single sheet
 *    with its own QR and no physical back — the flow skips the back step for
 *    `-digital`/`-open` keys.
 *  - RG (old ID): front + back, no QR.
 *  - Carteira de Pescador: front + back, no QR.
 *  - Certidão de Nascimento: single sheet; the modern one has a validation QR.
 */
/**
 * Family key used for the manual "Other / not listed" pick in the fallback
 * document-type picker (see {@link DOC_META}). Kept out of the picker's listed
 * families so it can be offered as the explicit last option.
 */
export const OTHER_FAMILY = 'outro';

export const DOC_META: Record<string, DocMeta> = {
  cin: { nome: 'Identidade (CIN)', icon: 'badge', temVerso: true, qr: 'back', cpf: 'required' },
  cnh: { nome: 'Carteira de Motorista (CNH)', icon: 'directions_car', temVerso: true, qr: 'inner', cpf: 'required' },
  rg: { nome: 'Identidade (RG)', icon: 'badge', temVerso: true, qr: 'none', cpf: 'ackIfMissing' },
  pescador: { nome: 'Carteira de Pescador', icon: 'phishing', temVerso: true, qr: 'none', cpf: 'ackIfMissing' },
  certidaonasc: { nome: 'Certidão de Nascimento', icon: 'description', temVerso: false, qr: 'front', cpf: 'ackIfMissing' },
  // Chosen when the user manually picks "Other / not listed" in the fallback type
  // picker. We can't know the layout, so we take the SAFE option: treat it as a
  // two-sided document (always ask for a back — Veriff needs one) with no QR.
  [OTHER_FAMILY]: { nome: 'Outro documento', icon: 'description', temVerso: true, qr: 'none', cpf: 'ackIfMissing' },
};

/** Fallback for an unknown / unrecognised document. */
export const DEFAULT_META: DocMeta = {
  nome: 'Documento',
  icon: 'help_outline',
  temVerso: false,
  qr: 'none',
  cpf: 'ackIfMissing',
};

/** The family key (before the first dash) of a classifier type key. */
export function docFamily(type: string): string {
  return type.split('-')[0];
}

/** True when the captured type already shows both sides at once (an "open" doc). */
export function isOpen(type: string): boolean {
  return type.endsWith('-open');
}

/** True for the digital/app printout variants (already single-sided, QR-bearing). */
export function isDigital(type: string): boolean {
  return type.endsWith('-digital');
}

/** Look up the metadata for a classifier type key. */
export function metaFor(type: string): DocMeta {
  return DOC_META[docFamily(type)] ?? DEFAULT_META;
}
